import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Folder,
  Tag,
  Layers,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import categoriesService, { Category, Subcategory, Field } from '../../services/categories.service';
import { useTranslation } from 'react-i18next';
import { useLocalizedName } from '../../utils/localization';

interface FormData {
  name_french: string;
  name_english: string;
  category_id?: string;
  subcategory_id?: string;
}

const DataManagementPage: React.FC = () => {
  const { t } = useTranslation();
  const { getLocalizedName } = useLocalizedName();
  const [categories, setCategories] = useState<Category[]>([]);
  const [subcategories, setSubcategories] = useState<Subcategory[]>([]);
  const [fields, setFields] = useState<Field[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Selection states
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory | null>(null);
  const [selectedFields, setSelectedFields] = useState<Field[]>([]);
  
  // Form states
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showSubcategoryForm, setShowSubcategoryForm] = useState(false);
  const [showFieldForm, setShowFieldForm] = useState(false);
  const [editingItem, setEditingItem] = useState<{ type: 'category' | 'subcategory' | 'field', id: string | number } | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name_french: '',
    name_english: '',
    category_id: '',
    subcategory_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesData, subcategoriesData, fieldsData] = await Promise.all([
        categoriesService.getCategories(),
        categoriesService.getSubcategories(),
        categoriesService.getFields()
      ]);
      
      setCategories(categoriesData);
      setSubcategories(subcategoriesData);
      setFields(fieldsData);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };


  const resetForm = () => {
    setFormData({
      name_french: '',
      name_english: '',
      category_id: '',
      subcategory_id: ''
    });
    setEditingItem(null);
    setShowCategoryForm(false);
    setShowSubcategoryForm(false);
    setShowFieldForm(false);
  };

  const handleEdit = (type: 'category' | 'subcategory' | 'field', item: any) => {
    setEditingItem({ type, id: item.id });
    setFormData({
      name_french: item.name_french,
      name_english: item.name_english,
      category_id: item.category_id || '',
      subcategory_id: item.subcategory_id || ''
    });
    
    if (type === 'category') setShowCategoryForm(true);
    else if (type === 'subcategory') setShowSubcategoryForm(true);
    else if (type === 'field') setShowFieldForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      if (editingItem) {
        // Update existing item
        if (editingItem.type === 'category') {
          await categoriesService.updateCategory(editingItem.id as string, formData);
        } else if (editingItem.type === 'subcategory') {
          const { subcategory_id, ...subcategoryData } = formData;
          await categoriesService.updateSubcategory(editingItem.id as string, subcategoryData);
        } else if (editingItem.type === 'field') {
          const { category_id, ...fieldData } = formData;
          await categoriesService.updateField(editingItem.id as number, fieldData);
        }
      } else {
        // Create new item
        if (showCategoryForm) {
          const { category_id, subcategory_id, ...categoryData } = formData;
          await categoriesService.createCategory(categoryData);
        } else if (showSubcategoryForm) {
          const { subcategory_id, ...subcategoryData } = formData;
          await categoriesService.createSubcategory(subcategoryData as any);
        } else if (showFieldForm) {
          const { category_id, ...fieldData } = formData;
          await categoriesService.createField(fieldData as any);
        }
      }
      
      await loadData();
      resetForm();
    } catch (error) {
    }
  };

  const handleDelete = async (type: 'category' | 'subcategory' | 'field', id: string | number) => {
    let confirmMessage = '';
    
    if (type === 'category') {
      confirmMessage = 'Êtes-vous sûr de vouloir supprimer cette catégorie ?\n\n⚠️ ATTENTION: Cette action supprimera également TOUTES les sous-catégories et domaines associés (suppression en cascade).';
    } else if (type === 'subcategory') {
      confirmMessage = 'Êtes-vous sûr de vouloir supprimer cette sous-catégorie ?\n\n⚠️ ATTENTION: Cette action supprimera également TOUS les domaines associés (suppression en cascade).';
    } else if (type === 'field') {
      confirmMessage = 'Êtes-vous sûr de vouloir supprimer ce domaine ?';
    }
    
    if (!window.confirm(confirmMessage)) return;
    
    try {
      if (type === 'category') {
        await categoriesService.deleteCategory(id as string);
        // Reset selections when deleting category
        setSelectedCategory(null);
        setSelectedSubcategory(null);
        setSelectedFields([]);
      } else if (type === 'subcategory') {
        await categoriesService.deleteSubcategory(id as string);
        // Reset subcategory and fields selections when deleting subcategory
        setSelectedSubcategory(null);
        setSelectedFields([]);
      } else if (type === 'field') {
        await categoriesService.deleteField(id as number);
        // Remove field from selected fields
        setSelectedFields(prev => prev.filter(field => field.id !== id));
      }
      
      await loadData();
    } catch (error) {
      alert('Erreur lors de la suppression. Veuillez réessayer.');
    }
  };


  const handleCategorySelect = async (category: Category) => {
    setSelectedCategory(category);
    setSelectedSubcategory(null);
    setSelectedFields([]);
    
    try {
      const subcategoriesData = await categoriesService.getSubcategoriesByCategory(category.id);
      setSubcategories(subcategoriesData);
    } catch (error) {
    }
  };

  const handleSubcategorySelect = async (subcategory: Subcategory) => {
    setSelectedSubcategory(subcategory);
    setSelectedFields([]);

    try {
      const fieldsData = await categoriesService.getFieldsBySubcategory(subcategory.id);
      setSelectedFields(fieldsData);
    } catch (error) {
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('admin.dataManagement.title')}</h1>
          <p className="text-gray-600">{t('admin.dataManagement.subtitle')}</p>
        </div>
      </div>

      {/* Selection Interface */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Categories Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Folder className="h-5 w-5 mr-2 text-brand-primary" />
              {t('admin.dataManagement.categories')}
            </h2>
          </div>
          <div className="p-4">
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {categories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => handleCategorySelect(category)}
                  className={`w-full text-left p-3 rounded-lg border transition-all ${
                    selectedCategory?.id === category.id
                      ? 'border-brand-primary bg-brand-primary/5 text-brand-primary'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="font-medium">{getLocalizedName(category)}</div>
                  <div className="text-sm text-gray-500">
                    {category.name_french || category.name_english}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Subcategories Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Layers className="h-5 w-5 mr-2 text-brand-primary" />
              {t('admin.dataManagement.subcategories')}
            </h2>
            {selectedCategory && (
              <p className="text-sm text-gray-500 mt-1">
                Pour: {getLocalizedName(selectedCategory)}
              </p>
            )}
          </div>
          <div className="p-4">
            {selectedCategory ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {subcategories.map((subcategory) => (
                  <button
                    key={subcategory.id}
                    onClick={() => handleSubcategorySelect(subcategory)}
                    className={`w-full text-left p-3 rounded-lg border transition-all ${
                      selectedSubcategory?.id === subcategory.id
                        ? 'border-brand-primary bg-brand-primary/5 text-brand-primary'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="font-medium">{getLocalizedName(subcategory)}</div>
                    <div className="text-sm text-gray-500">
                      {subcategory.name_french || subcategory.name_english}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">{t('admin.dataManagement.selectCategoryFirst')}</p>
            )}
          </div>
        </div>

        {/* Fields Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Tag className="h-5 w-5 mr-2 text-brand-primary" />
              {t('admin.dataManagement.fields')}
            </h2>
            {selectedSubcategory && (
              <p className="text-sm text-gray-500 mt-1">
                Pour: {getLocalizedName(selectedSubcategory)}
              </p>
            )}
          </div>
          <div className="p-4">
            {selectedSubcategory ? (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {selectedFields.map((field) => (
                  <div
                    key={field.id}
                    className="p-3 rounded-lg border border-gray-200 bg-gray-50"
                  >
                    <div className="font-medium text-sm">{getLocalizedName(field)}</div>
                    <div className="text-xs text-gray-500">{field.name_english}</div>
                  </div>
                ))}
                {selectedFields.length === 0 && (
                  <p className="text-gray-500 text-center py-4">{t('admin.dataManagement.noFieldsFound')}</p>
                )}
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">{t('admin.dataManagement.selectCategoryFirst')}</p>
            )}
          </div>
        </div>
      </div>

      {/* Results Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Résultats de la sélection</h2>
          <button
            onClick={() => {
              resetForm();
              setShowCategoryForm(true);
            }}
            className="inline-flex items-center px-3 py-2 bg-brand-primary text-white text-sm font-medium rounded-lg hover:opacity-90 transition-all"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nouvelle Catégorie
          </button>
        </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom Français
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Nom Anglais
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {!selectedCategory && !selectedSubcategory && selectedFields.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-6 py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <Folder className="h-12 w-12 text-gray-300 mb-2" />
                        <p className="text-lg font-medium">Aucune sélection</p>
                        <p className="text-sm">Sélectionnez une catégorie, sous-catégorie ou domaine pour voir les actions disponibles</p>
                      </div>
                    </td>
                  </tr>
                )}
                {selectedCategory && (
                  <tr className="bg-blue-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-900">
                      Catégorie
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {selectedCategory.name_french}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {selectedCategory.name_english}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit('category', selectedCategory)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                          title="Modifier la catégorie"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            resetForm();
                            setFormData(prev => ({ ...prev, category_id: selectedCategory.id }));
                            setShowSubcategoryForm(true);
                          }}
                          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded"
                          title="Ajouter une sous-catégorie"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete('category', selectedCategory.id)}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                          title="Supprimer la catégorie (et toutes ses sous-catégories et domaines)"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {selectedSubcategory && (
                  <tr className="bg-green-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-green-900">
                      Sous-catégorie
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {selectedSubcategory.name_french}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {selectedSubcategory.name_english}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit('subcategory', selectedSubcategory)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                          title="Modifier la sous-catégorie"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => {
                            resetForm();
                            setFormData(prev => ({ ...prev, subcategory_id: selectedSubcategory.id }));
                            setShowFieldForm(true);
                          }}
                          className="p-1 text-green-600 hover:text-green-800 hover:bg-green-100 rounded"
                          title="Ajouter un domaine"
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete('subcategory', selectedSubcategory.id)}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                          title="Supprimer la sous-catégorie (et tous ses domaines)"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {selectedFields.map((field) => (
                  <tr key={field.id} className="bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      Domaine
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {getLocalizedName(field)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {field.name_english}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleEdit('field', field)}
                          className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                          title="Modifier le domaine"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete('field', field.id)}
                          className="p-1 text-red-600 hover:text-red-800 hover:bg-red-100 rounded"
                          title="Supprimer le domaine"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      {/* Category Form Modal */}
      {showCategoryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingItem ? 'Modifier la catégorie' : 'Nouvelle catégorie'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom français
                </label>
                <input
                  type="text"
                  value={formData.name_french}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_french: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom anglais
                </label>
                <input
                  type="text"
                  value={formData.name_english}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_english: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Sauvegarder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Subcategory Form Modal */}
      {showSubcategoryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingItem ? 'Modifier la sous-catégorie' : 'Nouvelle sous-catégorie'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Catégorie
                </label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, category_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  required
                >
                  <option value="">Sélectionner une catégorie</option>
                  {categories.map(category => (
                    <option key={category.id} value={category.id}>
                      {category.name_french}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom français
                </label>
                <input
                  type="text"
                  value={formData.name_french}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_french: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom anglais
                </label>
                <input
                  type="text"
                  value={formData.name_english}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_english: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Sauvegarder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Field Form Modal */}
      {showFieldForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingItem ? 'Modifier le domaine' : 'Nouveau domaine'}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sous-catégorie
                </label>
                <select
                  value={formData.subcategory_id}
                  onChange={(e) => setFormData(prev => ({ ...prev, subcategory_id: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  required
                >
                  <option value="">Sélectionner une sous-catégorie</option>
                  {subcategories.map(subcategory => (
                    <option key={subcategory.id} value={subcategory.id}>
                      {subcategory.name_french}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom français
                </label>
                <input
                  type="text"
                  value={formData.name_french}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_french: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nom anglais
                </label>
                <input
                  type="text"
                  value={formData.name_english}
                  onChange={(e) => setFormData(prev => ({ ...prev, name_english: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-primary focus:border-transparent"
                  required
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-brand-primary text-white rounded-lg hover:opacity-90"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Sauvegarder
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataManagementPage;
